import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, database } from '../config/firebase';
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  setPersistence,
  browserSessionPersistence
} from 'firebase/auth';
import { ref, get, set } from 'firebase/database';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Set session-only persistence on mount
  useEffect(() => {
    const setSessionPersistence = async () => {
      try {
        // Set persistence to SESSION - auth state only persists in current tab/window
        await setPersistence(auth, browserSessionPersistence);
      } catch (error) {
        console.error('Error setting auth persistence:', error);
      }
    };

    setSessionPersistence();
  }, []);

  // Login function
  const login = async (email, password) => {
    try {
      // Ensure session persistence is set before login
      await setPersistence(auth, browserSessionPersistence);

      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Try to fetch user profile from database
      const userRef = ref(database, `users/${user.uid}`);
      const snapshot = await get(userRef);

      let profile;
      if (snapshot.exists()) {
        profile = snapshot.val();
        // Ensure email field exists for partner lookup (fix for legacy profiles)
        if (!profile.email) {
          profile.email = user.email;
          await set(userRef, profile);
        }
      } else {
        // Create a default profile from email if none exists
        const username = user.email.split('@')[0]; // Extract "omi" from "omi@ldr-webapp.com"
        profile = {
          name: username.charAt(0).toUpperCase() + username.slice(1), // Capitalize first letter
          role: 'user',
          email: user.email // Store email for partner lookup
        };

        // Persist the profile to Firebase
        await set(userRef, profile);
      }

      // Detect and update user's timezone (store under UID, not name)
      const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const timezoneRef = ref(database, `users/${user.uid}/lastTimezone`);
      await set(timezoneRef, detectedTimezone);

      setUserProfile(profile);
      return { success: true, user, profile };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await signOut(auth);
      setCurrentUser(null);
      setUserProfile(null);
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      return { success: false, error: error.message };
    }
  };

  // Monitor auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);

        // Try to fetch user profile
        const userRef = ref(database, `users/${user.uid}`);
        const snapshot = await get(userRef);

        if (snapshot.exists()) {
          let profile = snapshot.val();
          // Ensure email field exists for partner lookup (fix for legacy profiles)
          if (!profile.email) {
            profile.email = user.email;
            await set(userRef, profile);
          }
          setUserProfile(profile);
        } else {
          // Create default profile from email
          const username = user.email.split('@')[0];
          const profile = {
            name: username.charAt(0).toUpperCase() + username.slice(1),
            role: 'user',
            email: user.email
          };

          // Persist the profile to Firebase
          await set(userRef, profile);
          setUserProfile(profile);
        }
      } else {
        setCurrentUser(null);
        setUserProfile(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    login,
    logout,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
